import React, {
  useContext, useRef, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Animated,
} from 'react-native';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';

import SimpleDetail from './SimpleDetail';
import useMetascraper from '../../hooks/useMetascraper';
import { useOpenCalendarEvent } from '../../modules/calendar/actions';
import { useColors } from '../../contexts/theme';
import l, { timeString } from '../../utils/localization';
import { navigateToItem } from '../../modules/navigation/actions';
import MultiSelectContext from '../../contexts/MultiSelect';
import { DraggableFlatListContext } from '../DraggableFlatList/contexts/DraggableFlatListContext';
import MultiSelector from './MultiSelector';


const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    flex: 1,
    height: '100%',
  },
  boxInner: {
    alignItems: 'center',
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: 'gray',
    borderTopWidth: StyleSheet.hairlineWidth,
    flex: 1,
    justifyContent: 'flex-start',
    zIndex: 99,
    shadowOpacity: 0.3,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowRadius: 2,
  },
  titleContainer: {
    alignItems: 'center',
    flex: 1,
  },
});

const Detail = ({
  index, item, data, onPress,
}) => {
  const { data: metadata, loading: loadingMetadata } = useMetascraper(item.links && item.links[0]);

  const actions = item.links ? item.links.map((link) => ({ url: link, type: 'link' })) : [],
        { image } = metadata || {};

  const title = !metadata ? item.title : metadata.title;
  const subtitle = metadata ? metadata.description || '' : '';

  const previous = data[index - 1],
        showPostponedTime = !previous || (previous.isCalendarEvent
          ? item.postponedUntil && previous.endTime && previous.endTime.valueOf() !== item.postponedUntil.valueOf()
          : previous.status === 'postponed' && previous.postponedUntil && item.postponedUntil ? previous.postponedUntil.valueOf() !== item.postponedUntil.valueOf() : item.postponedUntil);

  const time = item.isCalendarEvent
    ? item.timeDescription
    : item.status === 'postponed' && showPostponedTime
      ? `${l.after} ${dayjs(item.postponedUntil).format(timeString)}`
      : null;

  //
  return (
    <SimpleDetail
      actions={actions}
      title={title}
      image={image}
      subtitle={subtitle}
      time={time}
      dotWeight={item.isCalendarEvent ? (item.endDate - item.startDate) / 400000 : 0}
      dotColor={item.color}
      onPress={onPress}
      loadingMetadata={loadingMetadata}
    />
  );
};


const ItemDetail = ({ item, index, isInAction }) => {
  const colors = useColors(),
        openCalendarEvent = useOpenCalendarEvent(),
        { isEnabled } = useContext(MultiSelectContext),
        [showMultiSelect, setShowMultiSelect] = useState(false),
        { data } = useContext(DraggableFlatListContext),
        multiSelectProgress = useRef(new Animated.Value(0)),
        dispatch = useDispatch(),
        { toggle } = useContext(MultiSelectContext);

  const onPress = () => {
    if (item.isCalendarEvent) {
      openCalendarEvent(item.id);
    } else if (showMultiSelect) {
      toggle(item.id);
    } else {
      dispatch(navigateToItem(item));
    }
  };

  useEffect(() => {
    if (!item.isCalendarEvent) {
      setShowMultiSelect(true);

      Animated.timing(multiSelectProgress.current, {
        toValue: isEnabled ? 1 : 0,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (!isEnabled && finished) {
          setShowMultiSelect(isEnabled);
        }
      });
    }
  }, [isEnabled, isInAction, item.isCalendarEvent, multiSelectProgress]);

  return (
    <View
      elevation={2}
      style={[styles.boxInner, {
        backgroundColor: item.isCalendarEvent ? colors.separator : colors.surface,
        borderColor: item.isCalendarEvent ? colors.surface : colors.separator,
      }]}
    >
      <Animated.View style={styles.wrapper}>
        <Animated.View style={[styles.titleContainer, {
          transform: !item.isCalendarEvent ? [{
            translateX: multiSelectProgress.current.interpolate({
              inputRange: [0, 1],
              outputRange: [0, 50],
            }),
          }] : [],
        }]}
        >
          <Detail
            item={item}
            index={index}
            data={data}
            onPress={onPress}
          />
        </Animated.View>
      </Animated.View>
      <MultiSelector
        item={item}
        hidden={item.isCalendarEvent}
        showMultiSelect={showMultiSelect}
        multiSelectProgress={multiSelectProgress}
      />
    </View>
  );
};

export default ItemDetail;
